/**
 * Invoice Store
 * Manages invoice state
 */

import { create } from 'zustand';
import {
  generateInvoice,
  getInvoices,
  updateInvoice,
  finalizeInvoice,
  sendPaymentReminder,
  subscribeToInvoices,
  createRetainerInvoice,
  sendInvoice,
  deleteInvoice,
  voidInvoice,
} from '../services/invoiceService';

export const useInvoiceStore = create((set, get) => ({
  invoices: [],
  loading: false,
  error: null,

  // Subscribe to invoices
  subscribeToInvoices: (userId, isAdmin = false, filters = {}) => {
    return subscribeToInvoices(userId, (invoices) => {
      set({ invoices });
    }, isAdmin, filters);
  },

  // Generate invoice
  generateInvoice: async (invoiceData) => {
    try {
      set({ loading: true, error: null });
      const result = await generateInvoice(invoiceData);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Update invoice
  updateInvoice: async (invoiceId, updateData, isAdmin = false) => {
    try {
      set({ loading: true, error: null });
      const result = await updateInvoice(invoiceId, updateData, isAdmin);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Finalize invoice
  finalizeInvoice: async (invoiceId) => {
    try {
      set({ loading: true, error: null });
      const result = await finalizeInvoice(invoiceId);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Send reminder
  sendReminder: async (invoiceId) => {
    try {
      set({ loading: true, error: null });
      const result = await sendPaymentReminder(invoiceId);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Get invoice by ID
  getInvoiceById: (invoiceId) => {
    return get().invoices.find(i => i.id === invoiceId);
  },

  // Create retainer invoice
  createRetainerInvoice: async (userId, retainerData) => {
    try {
      set({ loading: true, error: null });
      const result = await createRetainerInvoice(userId, retainerData);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Send invoice to client
  sendInvoice: async (invoiceId) => {
    try {
      set({ loading: true, error: null });
      const result = await sendInvoice(invoiceId);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Delete draft invoice
  deleteInvoice: async (invoiceId) => {
    try {
      set({ loading: true, error: null });
      const result = await deleteInvoice(invoiceId);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Void invoice
  voidInvoice: async (invoiceId, reason = '') => {
    try {
      set({ loading: true, error: null });
      const result = await voidInvoice(invoiceId, reason);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
}));
